// frontend/src/pages/RoleRedirect.jsx
import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, token, { signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    signal,
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : {};

  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return data?.data ?? data;
}

export default function RoleRedirect() {
  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();
  const { isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Checking your account…");

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    const ac = new AbortController();

    (async () => {
      try {
        if (!isSignedIn) {
          setStatus("Not signed in. Redirecting…");
          navigate("/login", { replace: true });
          return;
        }

        setStatus("Fetching session…");
        const token = await getToken().catch(() => null);

        if (!token) {
          setStatus("No session token. Redirecting…");
          navigate("/login", { replace: true });
          return;
        }

        setStatus("Loading profile…");

        let me;
        try {
          me = await fetchJSON("/api/users/me", token, { signal: ac.signal });
          console.log("[role-redirect] /api/users/me =", me);
        } catch (e) {
          console.error("[role-redirect] /api/users/me failed:", e);

          if (e?.status === 401 || e?.status === 403) {
            setStatus("Session expired. Redirecting…");
            navigate("/login", { replace: true });
            return;
          }

          setStatus("Could not load your profile. Please refresh and try again.");
          return;
        }

        const role = String(me?.role || "").trim().toLowerCase();

        if (!role) {
          console.error("[role-redirect] missing role from /api/users/me:", me);
          setStatus("Could not determine your role. Please refresh and try again.");
          return;
        }

        const adminRoles = new Set([
          "admin",
          "union_admin",
          "super_admin",
          "conference_admin",
          "district_admin",
        ]);

        if (adminRoles.has(role)) {
          setStatus("Admin detected. Redirecting…");
          navigate("/dashboard/admin", { replace: true });
          return;
        }

        if (role === "instructor") {
          setStatus("Instructor detected. Redirecting…");
          navigate("/dashboard/instructor", { replace: true });
          return;
        }

        const onboardingComplete =
          me?.onboarding_complete === true || me?.onboardingComplete === true;

        console.log(
          "[role-redirect] role =",
          role,
          "onboardingComplete =",
          onboardingComplete
        );

        if (role === "learner" && onboardingComplete !== true) {
          setStatus("Finishing your profile…");
          navigate("/onboarding", { replace: true });
          return;
        }

        if (role === "learner") {
          setStatus("Redirecting to learner dashboard…");
          navigate("/dashboard/learner", { replace: true });
          return;
        }

        console.warn("[role-redirect] unexpected role:", role, me);
        setStatus("Could not determine where to send you.");
      } catch (err) {
        console.error("[role-redirect] error:", err);
        setStatus("Something went wrong while checking your account.");
      }
    })();

    return () => ac.abort();
  }, [authLoaded, userLoaded, isSignedIn, getToken, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-14 w-14 border-4 border-orange-200 border-t-orange-600 mx-auto mb-4" />
        <p className="text-gray-700 font-semibold">{status}</p>
        <p className="text-gray-500 text-sm mt-2">This usually takes a moment.</p>
      </div>
    </div>
  );
}